import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { person } from "ionicons/icons";
import { IdentificationMethodEnum, isPasswordSignInMethod } from "../../../../types/profile";
import Container from "../../../atoms/Container";
import { TextField, FormGroup } from "../../../atoms/LoginTextField";
import InscriptionIdentifier from "../../../molecules/Home/Inscription/InscriptionIdentifier";
import * as Yup from "yup";
import { useFormik } from "formik";
import NextPrevButtons from "../../../molecules/NextPrevButtons";
import { postInitIdentification } from "../../../../services/user.service";
import { useConfig } from "../../../../contexts/ConfigContext";
import useQuery from "../../../../hooks/useQuery";
import { postInitIdentificationType } from "../../../../types/user.types";
import { useAuth } from "../../../../contexts/AuthContext";
import { useHomeContext } from "../../../../pages/Home/HomeContent";
import { WelcompeStepEnum } from "../../../../types/welcome";

interface Props {}

const DisplayInitIdentification: FC<Props> = () => {
  const query = useQuery();
  const { t } = useTranslation();
  const { startSession } = useAuth();

  const { setCurrentStep, welcomeData } = useHomeContext();
  const { study, selectedRole } = useConfig();
  // Resolve the role to set up: prefer the actual INVITED role (matched by the invitation's
  // profile id), then the selected role, then the subject (roles[0]). This avoids reading
  // `.access` off undefined when the invited role hasn't settled into selectedRole yet.
  const profileId = welcomeData?.invitations?.to_user?.profile;
  const role =
    welcomeData?.roles?.find((r) => r.id === profileId) ??
    selectedRole ??
    welcomeData?.roles?.[0];
  const access = role?.access;
  // The invited user's email is fixed on their account (they were invited at it); show it
  // read-only so they can confirm which account they're activating before setting a password.
  const invitedEmail = welcomeData?.invitations?.to_user?.email;

  const onNextClick = () => {
    setCurrentStep(WelcompeStepEnum.REGISTRED_SUCCESSFULLY);
  };

  const { initialValues, validationSchema } = useMemo(
    function () {
      let initialValues = {};
      let validationSchema = Yup.object().shape({});

      if (access?.signInMode === IdentificationMethodEnum.SECRET_QUESTIONS) {
        const obj = {};
        access.secretQuestions.forEach((question) => {
          obj[question.id] = Yup.string().required();
          initialValues[question.id] = "";
        });
        validationSchema = Yup.object().shape(obj);
      } else if (isPasswordSignInMethod(access?.signInMode)) {
        initialValues = {
          password: "",
          confirmPassword: "",
        };
        validationSchema = Yup.object().shape({
          password: Yup.string()
            .matches(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])(?!.*\s).{8,100}$/,
              "form_error_email_password"
            )
            .required()
            .oneOf([Yup.ref('confirmPassword')], "form_error_email_confirm_password"),
          confirmPassword: Yup.string()
            .required()
        });
      }

      return { initialValues, validationSchema };
    },
    [
      access?.signInMode,
      access?.secretQuestions,
    ]
  );

  async function onSubmit(values) {
    const invitation_token = query.get("invitation");
    const body: postInitIdentificationType = {
      invitation_token: invitation_token,
    };

    if (access?.signInMode === IdentificationMethodEnum.SECRET_QUESTIONS) {
      body.identification_questions = [];
      access.secretQuestions.forEach((question) => {
        body.identification_questions.push({
          id: question.id,
          answer: values[question.id],
          version: 1,
        });
      });
    }

    if (isPasswordSignInMethod(access?.signInMode)) {
      body.password = values.password;
    }

    const {
      data: { token },
    } = await postInitIdentification({ studyId: study.id }, body);

    await startSession(token);
    // history.push(`/${study.slug}/resonance/home`);
    onNextClick();
  }

  const { values, touched, errors, submitForm, setFieldValue } = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
  });

  return (
    <Container>
      {invitedEmail && (
        <FormGroup>
          <TextField
            icon={person}
            type="email"
            value={invitedEmail}
            onChange={() => undefined}
            disabled
            label={t("home_login_email_placeholder")}
            placeholder={t("home_login_email_placeholder")}
          />
        </FormGroup>
      )}
      <InscriptionIdentifier
        setFieldValue={setFieldValue}
        values={values}
        errors={errors}
        touched={touched}
        access={access}
      />

      <NextPrevButtons onNextClick={submitForm} />
    </Container>
  );
};

export default DisplayInitIdentification;
